/**
 * Local MIDI library — IndexedDB on device (no cloud cost).
 * Users can re-open imported files across sessions and delete them.
 */

const DB_NAME = "upt-midi-library";
const DB_VERSION = 1;
const STORE = "midis";

export interface SavedMidiMeta {
  id: string;
  title: string;
  noteCount: number;
  bpm: number;
  timeSignature: string;
  savedAt: number;
  byteLength: number;
}

export interface SavedMidiEntry extends SavedMidiMeta {
  bytes: ArrayBuffer;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

export async function listSavedMidis(): Promise<SavedMidiMeta[]> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const all = await idbReq(tx.objectStore(STORE).getAll() as IDBRequest<SavedMidiEntry[]>);
    db.close();
    return (all ?? [])
      .map(({ id, title, noteCount, bpm, timeSignature, savedAt, byteLength }) => ({
        id,
        title,
        noteCount,
        bpm,
        timeSignature,
        savedAt,
        byteLength,
      }))
      .sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

export async function getSavedMidi(id: string): Promise<SavedMidiEntry | null> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const row = await idbReq(tx.objectStore(STORE).get(id) as IDBRequest<SavedMidiEntry | undefined>);
    db.close();
    return row ?? null;
  } catch {
    return null;
  }
}

export async function saveMidiFile(
  bytes: ArrayBuffer,
  meta: Omit<SavedMidiMeta, "id" | "savedAt" | "byteLength"> & { id?: string },
): Promise<SavedMidiMeta> {
  const entry: SavedMidiEntry = {
    id: meta.id ?? `midi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: meta.title.slice(0, 80) || "Imported MIDI",
    noteCount: meta.noteCount,
    bpm: meta.bpm,
    timeSignature: meta.timeSignature,
    savedAt: Date.now(),
    byteLength: bytes.byteLength,
    bytes,
  };
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  await idbReq(tx.objectStore(STORE).put(entry));
  db.close();
  const { bytes: _b, ...rest } = entry;
  return rest;
}

export async function deleteSavedMidi(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  await idbReq(tx.objectStore(STORE).delete(id));
  db.close();
}
